import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import {css, Global, keyframes} from '@emotion/react';
import {BrowserRouter as Router, Switch, Route, Redirect, useHistory, Link, useParams} from 'react-router-dom';

import api, { APIError } from '../api';
import config from '../config';

import {Wrap, Float, Title} from '../components/common';
import {BtnBorder} from '../components/buttons';

import NoCamera from 'url:../images/no-camera.svg';
import IconPlay from 'url:../images/icon-play.svg';

import ConfirmationTest from '../components/confirmation_test';
import {MeasurementTest, Result} from '../components/measurement_test';
import Duration from '../duration';
import {Helmet} from 'react-helmet';
import Checkbox from '../components/checkbox';
import semver from 'semver';
import moment from 'moment';

const {siteName} = config;

export default () => {
  const {subject_number} = useParams();
  const [subject, setSubject] = useState();
  const [attend_history, setAttendHistory] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [panic, setPanic] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/web/version')
        .then(rs => {
          const v = rs.data;
          if (semver.gt(v, config.version)) {
            window.location.href = `${config.base}/attend/${subject_number}`;
          }
        }),
      api.get('/subjects/'+subject_number)
        .then((res) => {
          setSubject(res.data);
        }),
      api.get('/attend/'+subject_number)
        .then((res) => {
          setAttendHistory(res.data);
        })
        .catch(() => {})
    ])
      .then(() => setLoaded(true))
      .catch(err => setPanic(err));
  }, []);

  if ( panic ) throw panic;

  if (!loaded) {
    return (<Fragment></Fragment>);
  }

  return <AttendMain subject={subject} checkOnly={attend_history&&attend_history.status==4}/>;
}

const AttendMain = ({subject, checkOnly}) => {
  const history = useHistory();
  const {subject_number} = useParams();
  const [mode, setMode] = useState('none');
  const [confirmation_test_answers, setConfirmationTestAnswers] = useState([]);
  const [panic, setPanic] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [pictures, setPictures] = useState([]);
  const [available, setAvailable] = useState(false);
  const [denial, setDenial] = useState(false);
  const [usecamera, setUsecamera] = useState(false);
  const [restarted, setRestarted] = useState(false);
  const $video = useRef();
  const $cam = useRef();
  const $pict = useRef();
  const $stream = useRef();
  const [illegal, setIllegal] = useState(false);
  const [uploaded, setUploaded] = useState(false);

  useEffect(() => {
    if (!illegal) return;
    doCancel();
    alert('カメラの動作が確認できません。学科を中断いたします');
    history.push('/');
  }, [illegal]);

  const shutter = () => {
    if ( checkOnly ) return;

    if (!$stream.current.active || !$stream.current.getVideoTracks().every(t => t.enabled && !t.muted)) {
      setIllegal(true);
      return;
    }

    const cw = $pict.current.width;
    const ch = $pict.current.height;
    const $cv = $pict.current;
    const ctx = $cv.getContext("2d");
    const vw = $cam.current.videoWidth;
    const vh = $cam.current.videoHeight;
    const ah = vh / ch;
    const aw = vw / ah / cw;
    const a = Math.max(ah * aw, ah);
    const sw = vw / a;
    const sh = vh / a;

    ctx.fillStyle = "rgb(0, 0, 0)";
    ctx.fillRect(0, 0, cw, ch);
    ctx.drawImage($cam.current, (cw - sw) / 2, (ch - sh) / 2, sw, sh);

    return $cv.toDataURL("image/jpeg", 0.6);
  };

  const takeFace = useCallback(() => {
    const picture = shutter();
    const picts = pictures.slice();
    picts.push(picture);
    setPictures(picts);
    api.put('/attend/'+subject_number+'/log/picture/' + picts.length, {picture: picture})
      .then(_ => {
        if ( picts.length >= 3 ) {
          setUploaded(true);
        }
      })
      .catch(error => {
        setPanic(error);
      });
  }, [pictures]);

  const takeSnapshot = async () => {
    const picture = shutter();
    if (!picture) return;

    try {
      await api.post(`/attend/${subject_number}/events/snapshot`, {picture});
    } catch(err) {
      if (err.code == 401) {
        setPanic(err);
      } else {
        console.error(err);
      }
    }
  };

  const doAgree = () => {
    window.scrollTo(0, 0);
    setMode('preview');
    setUsecamera(true);
  };

  const doCheckVideo = () => {
    setMode('video');
    doStart();
  };

  const doRestart = () => {
    startVideo();
    setRestarted(true);
  };

  const doStart = () => {
    const sign = sessionStorage.getItem('SIGN');
    api.patch('/attend/'+subject_number+'/start', {sign})
      .then((res) => {
        startVideo();
      })
      .catch(error => {
        if (error.code == 403) {
          alert('2段階認証の有効期限が切れました');
          sessionStorage.removeItem('SIGN');
          history.push('/');
        } else {
          setPanic(error);
        }
      });
  };

  const doSave = (answer) => {
    const a = confirmation_test_answers.slice();
    a.push(answer);
    api.put('/attend/'+subject_number+'/log/confirmation/' + a.length)
      .catch(error => {
        setPanic(error);
      });
    setConfirmationTestAnswers(a);
  };

  const doNext = () => {
    $video.current.play();
  };

  const doCancel = () => {
    if ( ! checkOnly ) {
      api.patch('/attend/'+subject_number+'/cancel');
    }
    stopCamera();
  };

  const finish = answers => {
    setMode('result');
    setAnswers(answers);
  };

  const stopCamera = () => {
    $stream.current.getVideoTracks().forEach((track) => {
        track.stop();
    });
  }

  const startVideo = () => {
    $video.current.play();

    $video.current.addEventListener('ended', (event) => {
      if ( ! checkOnly ) {
        setMode('test');
        stopCamera();
      } else {
        setMode('result');
      }
    });

    $video.current.addEventListener('seeked', () => {
      setIllegal(true);
    });
  };

  useEffect(() => {
    if ( checkOnly ) {
      setMode('video');
    } else {
      setMode('agree');
    }
  }, [])

  if ( panic ) {
    throw panic;
  }
  if ( mode == 'none' ) return <></>

  return (
    <>
      <Helmet>
        <title>受講 | {siteName}</title>
      </Helmet>
    <Wrap>
      <div css={css`padding-top: 3rem`}>
        <Title name={subject.title}/>
      </div>
      {usecamera &&
        <>
          <Camera
            $cam={$cam}
            $pict={$pict}
            $stream={$stream}
            denial={denial}
            setAvailable={setAvailable}
            setDenial={setDenial}
            hide={mode != 'preview'}/>
          <canvas width="640" height="480" ref={$pict} css={css`display: none;`}></canvas>
        </>
      }

      {mode == 'agree' && <Agree subject={subject} doAgree={doAgree}/>}
      {mode == 'preview' && <Preview subject={subject} doCheckVideo={doCheckVideo} $stream={$stream} available={available} denial={denial}/>}
      {mode == 'video' && <Watch
        subject={subject}
        checkOnly={checkOnly}
        $video={$video}
        restarted={restarted}
        doRestart={doRestart}
        doNext={doNext}
        doSave={doSave}
        doCancel={doCancel}
        setPanic={setPanic}
        setIllegal={setIllegal}
        takeFace={takeFace}
        takeSnapshot={takeSnapshot}/>
      }
      {mode == 'test' && uploaded && <MeasurementTest subject={subject} confirmation_test_answers={confirmation_test_answers} pictures={pictures} onFinish={finish}/>}
      {mode == 'test' && !uploaded &&
        <div css={css`
          text-align: center;
          color: #999999;
          margin: 60px auto;
        `}>
          写真のアップロード中です。<br/>アップロードが完了するまでしばらくお待ちください。
        </div>
      }
      {mode == 'result' && <Result subject={subject} answers={answers} checkOnly={checkOnly}/>}

    </Wrap>
    </>
  );
};

const Watch = ({subject, checkOnly, $video, restarted, doRestart, doNext, doSave, doCancel, setPanic, setIllegal, takeSnapshot, takeFace}) => {
  const [confirmation_test_index, setConfirmationTestIndex] = useState(-1);
  const [view_test, setViewTest] = useState(false);
  const [shutting_remind, setShuttingRemind] = useState(10);
  const [shutting, setShutting] = useState('none');
  const [flash, setFlash] = useState('none');
  const [lastSnap, setLastSnap] = useState(moment());
  const [tookSnap, setTookSnap] = useState();
  const [ticks, setTicks] = useState(0);

  useEffect(() => {
    const now = moment();
    if (!tookSnap && moment(lastSnap).add(180, 'seconds').isBefore(now)) {
      setTookSnap(takeSnapshot().then(() => setTookSnap()));
      setLastSnap(now);
    }
    const timeout = setTimeout(() => setTicks(ticks+1), 10000);
    return () => clearTimeout(timeout);
  }, [ticks, lastSnap]);

  useEffect(() => {
    let queue = [];

    subject.confirmation_test.map((val, i) => {
      const duration = Duration.parse(val.timing);
      queue.push({
        'action': 'question',
        'index': i,
        'timeline': duration,
      });
    });

    let metaLoaded = false;

    if ( ! checkOnly ) {
      const onMeta = () => {
        if (!$video.current.duration) {
          setPanic(new Error("duration = 0"));
        }

        queue = queue.concat(
          determineShotTimings($video.current.duration)
            .map(r => ({
              action: 'shutter',
              timeline: r - 3
            }))
        );

        metaLoaded = true;
      };

      $video.current.addEventListener('loadedmetadata', onMeta);
    } else {
      metaLoaded = true;
    }

    const actions = {
      question: val => {
        setConfirmationTestIndex(val.index);
        setViewTest(true);
        $video.current.pause();
      },
      shutter: () => {
        setShutting('start');
      }
    };

    const checkAuth = () => {
      api.get('/attend/check')
        .catch(error => {
          setPanic(error);
        });
    }

    $video.current.addEventListener('timeupdate', (event) => {
      if (!metaLoaded) {
        setPanic(new Error("no meta data loaded"));
      }

      if ($video.current.playbackRate > 1) {
        return setIllegal(true);
      }

      const current = $video.current.currentTime;
      queue = queue.filter(val => {
        if (val.timeline <= current) {
          actions[val.action](val);
          checkAuth();
          return false;
        } else {
          return true;
        }
      });
    });
  }, []);

  useEffect(() => {
    switch ( shutting ) {
      case 'start':
        setShuttingRemind(10);
        setShutting('countdown');
        break;
      case 'countdown': {
        if (shutting_remind == 0) {
          setShutting('shot');
        } else {
          const timeout = setTimeout(() => {
            const remind = shutting_remind - 1;
            setShuttingRemind(remind);
          }, 1000);
          return () => clearTimeout(timeout);
        }
      }
      case 'shot': {
        setFlash('start');
        takeFace();
        setShutting("end");
        break;
      }
      case 'end': {
        const timeout = setTimeout(() => {
          setShutting("none");
        }, 500);
        return () => clearTimeout(timeout);
      }
    }
  }, [shutting, shutting_remind, takeFace]);

  useEffect(() => {
    switch ( flash ) {
      case 'start': {
        const timeout = setTimeout(() => {
          setFlash('flashing');
        }, 250);
        return () => clearTimeout(timeout);
      }
      case 'flashing': {
        const timeout = setTimeout(() => {
          setFlash('none');
        }, 250);
        return () => clearTimeout(timeout);
      }
    }
  }, [flash]);

  return (
    <div css={css`
        width: 100vw;
        height: 100%;
        height: -webkit-fill-available;
        position: fixed;
        top: 0;
        left: 0;
    `}>
      <video css={css`width: 100%; height: 100%; display: block; background: black`} ref={$video} preload="metadata" playsInline disablePictureInPicture disableRemotePlayback controlsList="nodownload nofullscreen noremoteplayback" onContextMenu={ev => ev.preventDefault()}>
        <source src={config.api_url + subject.video_file} type="video/mp4"></source>
      </video>
      {checkOnly &&
        <div css={css`
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          height: -webkit-fill-available;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 6;
          background-color: rgba(0, 0, 0, 0.5);
          ${restarted && `display: none;`}
        `}>
          <img src={IconPlay} onClick={doRestart} width="60" css={css`cursor: pointer; &:hover {
            opacity: 0.6;
            transition: 0.5s all;
          }`}/>
        </div>
      }
      <div css={css`
        visibility: hidden;
        pointer-events: none;
        width: 100vw;
        overflow: hidden;
        position: absolute;
        top: 0;
        left: 0;
        height: 100vh;

        canvas {
          width: 640px;
          height: 480px;
        }
      `}>
      </div>
      {view_test && <ConfirmationTest test={subject.confirmation_test[confirmation_test_index]} limit="60" doNext={() => { setViewTest(false); doNext(); }} doSave={doSave} doCancel={doCancel}/>}
      <Shutter flash={flash} shutting={shutting} shuttingRemind={shutting_remind} />
    </div>
  )
}

export const Shutter = ({flash, shutting, shuttingRemind}) => (
  <div css={css`
    position: absolute;
    right: 22px;
    top: 22px;
    width: 150px;
    height: 120px;
    z-index: 20;
    background: ${flash == 'flashing' ? 'white' : '#B50D16'};
    border-radius: 8px;
    color: white;
    font-weight: bold;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    transition: transform 1s, background 0.25s, box-shadow 0.25s;
    transform: translate(calc(100% + 44px), 0);
    box-shadow: 0 0 2px 2px rgba(0, 0, 0, 0.25);
    ${shutting !== 'none' && `transform: translate(0, 0);` }
    `}>
      <div css={css`display: flex; align-items: center`}>
        <span className="material-icons-outlined" css={css`margin-right: 10px; ${flash == 'flashing' && `visibility: hidden;`}`}>face</span>顔写真撮影
      </div>
      <div css={css`line-height: 1`}>
        <span css={css`font-size: 64px; font-weight: bold; ${flash == 'flashing' && `visibility: hidden;`}`}>{shuttingRemind}</span>
      </div>
  </div>
);

const Backhome = () => (
  <div css={css`
    text-align: center;
    margin-top: 40px;
  `}>
    <Link to={`/`} css={css`color: #666; &:hover { color: black; }`}>ホームに戻る</Link>
  </div>
);

const Agree = ({subject, doAgree}) => {
  const [agree, setAgree] = useState(false);
  return (
    <>
      <div css={css`
        background-color: white; border: 1px solid #cccccc; padding: 30px;
        margin: 2rem 0 0;
        @media screen and (max-width: 720px) {
          padding: 20px;
        }
      `}>
        <p css={css`
          line-height: 1.8em;
          margin: 0;
          @media screen and (max-width: 720px) {
            font-size: 13px;
            line-height: 1.6em;
          }
        `}>
          学科受講においては、早送り・停止・巻き戻しはできません。<br/>
          また、以下の内容に同意いただいた上でご受講いただきますようお願いいたします。<br/>
          ・受講中にランダムで3回撮影が行われます。<br/>
          ・受講時にカメラがオンになっているようにお願いいたします。<br/>
          ・以下の場合には、最後まで視聴されたとしても単位が認定されません。最初から受講しなおしとなりますのでご注意ください。<br/>
          ①ご本人の受講が確認できない写真が1枚以上見受けられた場合（顔が画面に映っていない・画面が暗く、顔が判別できない等）<br/>
          ②ご入所時の写真と受講時の写真があきらかに別人である場合<br/>
          ③望ましくない受講態度が1枚以上見受けられた場合（離席・居眠り・携帯機器の使用・移動中・雑談・飲酒・喫煙等）<br/>
          ・途中と最後に学科問題が出題されます。こちらを未回答のまま放置されますと、受講されていないと判断され、動画は中断となります。最初から受講しなおしとなりますのでご注意ください。
        </p>
      </div>
      <div css={css`display: flex; justify-content: center; align-items: center; margin: 20px 0 0;`}>
        <Checkbox checked={agree} onChange={() => setAgree(agree ? false : true)}/><span css={css`color: #B50D16;margin-left: 5px; font-weight: bold; cursor: pointer`} onClick={() => setAgree(!agree)}>同意する</span>
      </div>
      <div css={css`display: flex; justify-content: center; align-items: center; margin: 20px 0 0;`}>
        <BtnBorder name="同意してカメラの設定へ" disabled={agree ? false : true} action={() => doAgree()}/>
      </div>
      {Backhome()}
    </>
  )
};

const determineShotTimings = duration => {
  const randRange = (min, max) => {
    return Math.floor(Math.random() * (max - min + 1) + min);
  };

  const block = duration / 8;
  const range1 = randRange(block, block * 2);

  const second_duration = duration - range1;
  const second_block = second_duration / 6;
  const range2 = randRange(second_block, second_block * 2);

  // 動画終了5分前から
  const third_offset = Math.max(range1 + range2 + 60, duration - 300);
  const third_duration = duration - third_offset;
  const range3 = randRange(0, third_duration - 60);
  return [range1, range1 + range2, third_offset + range3];
};

const Preview = ({subject, doCheckVideo, available, denial}) => {
  const history = useHistory();
  const [loaded, setLoaded] = useState(false);
  const $cam = useRef();
  const $pict = useRef();

  useEffect(() => {
    const constraints = {
      audio: false,
      video: {
        width: 640,
        height: 480,
        facingMode: "user"
      }
    };
  }, []);

  return (
    <>
      {available &&
        <div css={css`display: flex; flex-direction: column; align-items: center;`}>
          <p css={css`text-align: center; color: #999999;`}>顔が写っているか、判別できる明るさかを確認してください。</p>
          <div css={css`margin-top: 2rem`}>
            <BtnBorder name="受講を開始する" action={() => doCheckVideo()}/>
          </div>
          {Backhome()}
        </div>
      }
      {denial && <>
        <Float>
          <img src={NoCamera} width="96" height="96"/>
          <p css={css`font-size: 24px; margin: 0 0 30px;`}>カメラが利用出来ません</p>
          <p css={css`color: #666666; text-align: center;`}>
            学科を受講するにはカメラを有効にする必要があります。<br/>
            カメラの利用が不許可になっていないか？<br/>
            または、お使いの端末でカメラが利用出来るか、ご確認ください。
          </p>
          <div css={css`margin-top: 2rem`}>
            <BtnBorder name="トップに戻る" action={() => history.push('/')}/>
          </div>
          {Backhome()}
        </Float>
      </>}
    </>
  );
}
const Camera = ({$cam, $pict, $stream, denial, setAvailable, setDenial, hide}) => {
  useEffect(() => {
    const constraints = {
      audio: false,
      video: {
        width: 640,
        height: 480,
        facingMode: "user"
      }
    };

    navigator.mediaDevices.getUserMedia(constraints)
      .then((stream) => {
        $cam.current.srcObject = stream;
        $cam.current.onloadedmetadata = (e) => {
          $cam.current.play();
        };
        $stream.current = stream;
        setAvailable(true);
      }).catch((err) => {
        setDenial(true);
      })
      return () => {
        if ( $stream.current ) {
          const tracks = $stream.current.getTracks();
          tracks.forEach(function(track) {
            track.stop();
          });
          if ( $cam.current ) {
            $cam.current.srcObject = null;
          }
        }
      }
  }, []);

  const visibleCSS = css`
    position: relative;
    margin: 40px auto;
    max-width: 480px;
    width: 100%;
    border: 1px solid #EEEEEE;
  `;

  const hiddenCSS = css`
    position: absolute;
    left: 0;
    top: 0;
    width: 1px;
  `;

  return (
    <>
    {! denial && <div css={css`
      ${hide ? hiddenCSS : visibleCSS}
      &:before {
        content: "";
        display: block;
        width: 100%;
        padding-top: 75%;
      }
    `}>
      <video css={css`
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: 100%;
        background: #EEEEEE;
      `} ref={$cam} width="480" height="320" playsInline></video>
    </div>}
    </>
  )
};
